
import { Component, Prop, Watch } from "vue-property-decorator";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import annotationPlugin from "chartjs-plugin-annotation";
import Vue from "vue";
import ThemeUpdatingView from "@/components/general/ThemeUpdatingView.vue";
import camelcaseKeys from "camelcase-keys";
import { TimePeriod } from "@/models/public-bot-stats/TimePeriod";
import { yAxisInteractionLine } from "@/plugins/chartjs";
import { formatDateFromAPI } from "@/utils/dateUtils";
import {
  getCurrencyDisplayValue,
  getIndicatorDisplayValue,
} from "@/utils/currencyUtils";

@Component
export default class OBvsTopMarketsChart extends ThemeUpdatingView {
  @Prop()
  public timePeriod!: TimePeriod;

  // 📦 Dates for each portfolio check-up
  public dateLabels: Date[] = [];

  // 📦📈 Chart which displays the bot balance over time
  private obVsTopMarketsChart: Chart | any = null;

  // 📦 Dataset for OB's overall performance
  private obBotsRoiDataset: number[] = [];

  // 📦 Dataset for BTC's overall performance
  private btcDataset: number[] = [];

  // 📦 Dataset for ETH's overall performance
  private ethDataset: number[] = [];

  // 🚩 Flag for whether the chart's data is loading
  private isLoading: boolean = false;

  // 📦 The last time the data for the chart has been recalculated on the backend
  private lastUpdateTime: number | null = null;

  /**
   * 👂 Listener used in order to determine the selected chart filter mode
   */
  @Watch("dataset")
  private onDaysFilterChanged(val: number[], oldVal?: number[]) {
    this.createChart();
  }

  /**
   * 👂 Listener for changes in the selected time period
   */
  @Watch("timePeriod")
  private onSelectedTimePeriodChanged(val: number, oldVal: number) {
    this.fetchDataset();
  }

  protected themeUpdate() {
    this.createChart();
  }

  protected mounted() {
    Chart.register(annotationPlugin);
    this.fetchDataset();
  }

  /**
   * 🚀 Fetches the dataset from the API
   */
  private fetchDataset() {
    if (!this.timePeriod) {
      return;
    }

    this.isLoading = true;
    Vue.prototype.$api
      .post("/api/v1/botvsmarket", {
        weighted: false,
        with_stat_time: true,
        markets: ["BTC", "ETH"],
        strategies: ["Performer v2"],
        min_duration: 7,
        period:
          this.timePeriod == TimePeriod.FromStart ||
          this.timePeriod > TimePeriod.Week
            ? "day"
            : "hour",
        start_time: this.timePeriod == -1 ? undefined : this.timePeriod,
      })
      .then((response: any) => camelcaseKeys(response.data, { deep: true }))
      .then((response: any) => {
        let chartData = response.result;
        this.lastUpdateTime = response.statsTime.endTime;
        this.$emit("update-time", this.lastUpdateTime);

        this.dateLabels = Object.keys(chartData).map(
          (value: String, index: Number, array: String[]) => {
            return new Date(
              Number(value) * 1000 - new Date().getTimezoneOffset() * 60
            );
          }
        );
        this.obBotsRoiDataset = Object.values(chartData).map(
          (datasetEntry: any) => datasetEntry.botRoi
        );
        this.btcDataset = Object.values(chartData).map(
          (datasetEntry: any) => datasetEntry.btc
        );
        this.ethDataset = Object.values(chartData).map(
          (datasetEntry: any) => datasetEntry.eth
        );
        this.createChart();
      })
      .finally(() => (this.isLoading = false));
  }

  private get annotationsConfiguration(): any {
    return {
      clip: false,
      common: {
        drawTime: "afterDraw",
      },
      annotations: {
        obLatestLabel: {
          type: "label",
          xValue: this.dateLabels[this.dateLabels.length - 1],
          yValue: this.obBotsRoiDataset[this.obBotsRoiDataset.length - 1],
          xAdjust: 30,
          backgroundColor: this.$vuetify.theme.currentTheme.obIndexChartColor,
          color: this.$vuetify.theme.dark
            ? this.$vuetify.theme.currentTheme.base
            : "white",
          borderRadius: 3,
          padding: 4,
          content: [
            getIndicatorDisplayValue(
              this.obBotsRoiDataset[this.obBotsRoiDataset.length - 1]
            ),
          ],
        },
        btcLatestLabel: {
          type: "label",
          xValue: this.dateLabels[this.dateLabels.length - 1],
          yValue: this.btcDataset[this.btcDataset.length - 1],
          xAdjust: 30,
          backgroundColor: "#E8B844",
          color: "white",
          borderRadius: 3,
          padding: 4,
          content: [
            getIndicatorDisplayValue(
              this.btcDataset[this.btcDataset.length - 1]
            ),
          ],
        },
        ethLatestLabel: {
          type: "label",
          xValue: this.dateLabels[this.dateLabels.length - 1],
          yValue: this.ethDataset[this.ethDataset.length - 1],
          xAdjust: 30,

          backgroundColor: "#8087B1",
          color: "white",
          borderRadius: 3,
          padding: 4,
          content: [
            getIndicatorDisplayValue(
              this.ethDataset[this.ethDataset.length - 1]
            ),
          ],
        },
      },
    };
  }

  /**
   * 🎨 Draws a dynamic chart on the canvas which is responsible for displaying the portfolio
   */
  private createChart() {
    if (this.obVsTopMarketsChart == null) {
      let container: any = document.getElementById(
        "container-ob-vs-market-chart"
      );
      this.obVsTopMarketsChart = new Chart(container, {
        type: "line",
        data: {
          labels: this.dateLabels,
          datasets: this.formatChartDatasets(this.timePeriod),
        },
        options: {
          normalized: true,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: this.getTimeScale(this.timePeriod),
            y: this.getYAxes(),
          },
          layout: {
            padding: {
              right: 58,
            },
          },
          interaction: {
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                boxWidth: 12,
                boxHeight: 1,
                font: { family: "Aeroport" },
              },
            },
            tooltip: {
              intersect: false,
              mode: "index",
              callbacks: {
                title: this.getTooltipTitle,
                label: (context: any) => {
                  return (
                    " " +
                    context.dataset.label +
                    ": " +
                    Number(context.parsed.y).toFixed(2) +
                    "%"
                  );
                },
              },
            },
            annotation: this.annotationsConfiguration,
          },
        },
        plugins: [yAxisInteractionLine(this.$vuetify)],
      });
    } else {
      this.obVsTopMarketsChart.data.datasets = this.formatChartDatasets(
        this.timePeriod
      );
      this.obVsTopMarketsChart.data.labels = this.dateLabels;
      this.obVsTopMarketsChart.options.scales!!.x = this.getTimeScale(
        this.timePeriod
      );
      this.obVsTopMarketsChart.config.options!!.scales!!.y = this.getYAxes();
      this.updateLatestChartValueAnnotations();
      this.obVsTopMarketsChart.update();
    }
  }

  /**
   * 🏗 Updates the chart annotations for the latest value of each
   */
  private updateLatestChartValueAnnotations() {
    let obLabel = this.obVsTopMarketsChart.config.options.plugins.annotation
      .annotations.obLatestLabel;
    obLabel.yValue = this.obBotsRoiDataset[this.obBotsRoiDataset.length - 1];
    obLabel.content = getIndicatorDisplayValue(
      this.obBotsRoiDataset[this.obBotsRoiDataset.length - 1]
    );
    obLabel.backgroundColor = this.$vuetify.theme.currentTheme.obIndexChartColor;
    obLabel.color = this.$vuetify.theme.dark
      ? this.$vuetify.theme.currentTheme.base
      : "white";

    let btcLabel = this.obVsTopMarketsChart.config.options.plugins.annotation
      .annotations.btcLatestLabel;
    btcLabel.yValue = this.btcDataset[this.btcDataset.length - 1];
    btcLabel.content = getIndicatorDisplayValue(
      this.btcDataset[this.btcDataset.length - 1]
    );

    let ethLabel = this.obVsTopMarketsChart.config.options.plugins.annotation
      .annotations.ethLatestLabel;
    ethLabel.yValue = this.ethDataset[this.ethDataset.length - 1];
    ethLabel.content = getIndicatorDisplayValue(
      this.ethDataset[this.ethDataset.length - 1]
    );
  }

  /**
   * 🏗 Generates the tooltip title for each data point.
   *    dd/MM/yyyy - general format
   *    dd/MM/yyyy HH:ss z - for just the latest point as it updates throughout the day
   */
  private getTooltipTitle(context: any): any {
    if (!this.lastUpdateTime) {
      return formatDateFromAPI(context[0].parsed.x / 1000);
    }
    return context[0].dataIndex == this.obBotsRoiDataset.length - 1
      ? formatDateFromAPI(this.lastUpdateTime, true, true)
      : formatDateFromAPI(context[0].parsed.x / 1000);
  }

  /**
   * 🏗 Configures the format and properties for X-axis of the chart
   */
  private getTimeScale(timePeriod: TimePeriod): any {
    let configuration: any = {
      type: "timeseries",
      ticks: {
        color: this.$vuetify.theme.currentTheme.chartLabel,
        font: { family: "Aeroport" },
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
      stacked: true,
    };

    if (timePeriod == TimePeriod.Day) {
      configuration.time = {
        unit: "hour",
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }

    if ([TimePeriod.Week, TimePeriod.Month].includes(timePeriod)) {
      configuration.time = {
        unit: "day",
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }

    if (
      [
        TimePeriod.ThreeMonths,
        TimePeriod.SixMonths,
        TimePeriod.FromStart,
      ].includes(timePeriod)
    ) {
      configuration.time = {
        unit: "month",
        displayFormats: { day: "MMM YYYY" },
        color: this.$vuetify.theme.currentTheme.chartLabel,
      };
    }
    return configuration;
  }

  /**
   * 🏗 Assembles the datasets that will be displayed
   */
  private formatChartDatasets(timePeriod: TimePeriod): any {
    return [
      {
        label: "One Click Crypto",
        data: this.obBotsRoiDataset,
        borderColor: this.$vuetify.theme.currentTheme
          .obIndexChartColor as string,
        backgroundColor: this.$vuetify.theme.currentTheme
          .obIndexChartColor as string,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 3,
        tension: [TimePeriod.SixMonths, TimePeriod.FromStart].includes(
          timePeriod
        )
          ? 0.5
          : 0,
      },
      {
        label: "BTC",
        data: this.btcDataset,
        borderColor: "#E8B844",
        backgroundColor: "#E8B844",
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
        tension: [TimePeriod.SixMonths, TimePeriod.FromStart].includes(
          timePeriod
        )
          ? 0.5
          : 0,
      },
      {
        label: "ETH",
        data: this.ethDataset,
        borderColor: "#8087B1",
        backgroundColor: "#8087B1",
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderWidth: 2.5,
        tension: [TimePeriod.SixMonths, TimePeriod.FromStart].includes(
          timePeriod
        )
          ? 0.5
          : 0,
      },
    ];
  }

  /**
   * 🔨 Calculates a dynamic y axis based on the provided dataset
   */
  private getYAxes(): any {
    return {
      ticks: {
        callback: function(value: number, index: number, values: number[]) {
          return (
            (Math.max(...values) - Math.min(...values) >= 50
              ? value.toFixed(0)
              : value.toFixed(2)) + "%"
          );
        },
        color: this.$vuetify.theme.currentTheme.chartLabel,
        font: { family: "Aeroport" },
      },
      grid: {
        color: this.$vuetify.theme.currentTheme.chartGridColor,
        borderColor: this.$vuetify.theme.currentTheme.chartGridColor,
      },
    };
  }
}
